import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { RippleModule } from 'primeng/ripple';
import { ButtonModule } from 'primeng/button';
import { AuthService } from '../../../auth/components/services/auth.service';
import { distinctUntilChanged, filter, ReplaySubject, take, tap } from 'rxjs';
import { PanelMenuModule } from 'primeng/panelmenu';
import { MenuItem } from 'primeng/api';
import { NgIf } from '@angular/common';

@Component({
    selector: 'app-side-bar',
    imports: [RippleModule, ButtonModule, PanelMenuModule, NgIf],
    templateUrl: './side-bar.component.html',
    styleUrl: './side-bar.component.scss',
})
export class SideBarComponent implements OnInit, OnDestroy {
    router = inject(Router);
    authService = inject(AuthService);
    private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
    items: MenuItem[];
    currentRoute = '';
    name: string;
    storeName: string;

    ngOnInit() {
        this.getTheCurrentRoute();
        this.authService.me
            .pipe(
                take(1),
                distinctUntilChanged(),
                filter((user) => !!user),
                tap((user) => {
                    this.name = user.name;
                    this.storeName = user.storeName;
                    if (user.role === 'OWNER' || user.role === 'ADMIN') {
                        this.initAdminMenuItems();
                    } else if (user.role === 'CASHIER') {
                        this.initCashierMenuItems();
                    }
                }),
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }

    logOut() {
        this.authService.logout();
    }

    initCashierMenuItems(): void {
        this.items = [
            {
                key: '0',
                label: 'الكاشير',
                icon: 'pi pi-shopping-bag',
                routerLink: ['/pos'],
                routerLinkActiveOptions: { exact: true },
            },
            {
                label: 'الفواتير',
                icon: 'pi pi-file-edit',
                routerLink: ['/invoices'],
                routerLinkActiveOptions: { exact: false },
            },
            {
                label: 'عروض الاسعار',
                icon: 'pi pi-file-edit',
                routerLink: ['/quotes'],
                routerLinkActiveOptions: { exact: false },
            },
            // {
            //     label: 'المنتجات',
            //     icon: 'pi pi-box',
            //     routerLink: ['/products'],
            //     routerLinkActiveOptions: { exact: true }
            // }
        ].filter((i) => !!i);
    }

    initAdminMenuItems(): void {
        this.items = [
            {
                key: '0',
                label: 'لوحة التحكم',
                icon: 'pi pi-chart-bar',
                routerLink: ['/dashboard'],
                routerLinkActiveOptions: { exact: true },
            },
            {
                key: '1',
                label: 'الكاشير',
                icon: 'pi pi-shopping-bag',
                routerLink: ['/pos'],
                routerLinkActiveOptions: { exact: true },
            },
            {
                key: '2',
                label: 'الفواتير',
                icon: 'pi pi-file-edit',
                routerLink: ['/invoices'],
                routerLinkActiveOptions: { exact: false },
            },
            {
                key: '3',
                label: 'عروض الاسعار',
                icon: 'pi pi-file-edit',
                routerLink: ['/quotes'],
                routerLinkActiveOptions: { exact: false },
            },
            {
                key: '4',
                label: 'العملاء',
                icon: 'pi pi-users',
                routerLinkActiveOptions: { exact: false },
                expanded: this.isRouteActive(['customers', 'customer-payments', 'customer-requests']),
                items: [
                    {
                        label: 'قائمة العملاء',
                        routerLink: ['/customers'],
                        routerLinkActiveOptions: { exact: false },
                    },
                    {
                        label: 'سندات العملاء',
                        routerLink: ['/customer-payments'],
                        routerLinkActiveOptions: { exact: false },
                    },
                    {
                        label: 'سندات الاستلام',
                        routerLink: ['/customer-requests'],
                        routerLinkActiveOptions: { exact: false },
                    },
                ],
            },
            {
                label: 'المشتريات',
                icon: 'pi pi-shopping-cart',
                routerLink: ['/purchase-orders'],
                routerLinkActiveOptions: { exact: false },
            },
            {
                label: 'المرتجعات',
                icon: 'pi pi-shopping-cart',
                routerLink: ['/stock-returns'],
                routerLinkActiveOptions: { exact: false },
            },
            {
                label: 'الموردون',
                icon: 'pi pi-users',
                expanded: this.isRouteActive(['suppliers', 'supplier-bills', 'supplier-payments']),
                items: [
                    {
                        label: 'قائمة الموردين',
                        routerLink: ['/suppliers'],
                        routerLinkActiveOptions: { exact: false },
                    },
                    {
                        label: 'فواتير  مشتريات الموردين',
                        routerLink: ['/supplier-bills'],
                        routerLinkActiveOptions: { exact: false },
                    },
                    {
                        label: 'فواتير مرتجعات الموردين',
                        routerLink: ['/supplier-credit-notes'],
                        routerLinkActiveOptions: { exact: false },
                    },
                    {
                        label: 'سندات الموردين',
                        routerLink: ['/supplier-payments'],
                        routerLinkActiveOptions: { exact: false },
                    },
                ],
            },
            {
                label: 'المصروفات',
                icon: 'pi pi-book',
                routerLink: ['/expenses'],
                routerLinkActiveOptions: { exact: true },
            },
            {
                label: 'التقارير',
                icon: 'pi pi-book',
                routerLink: ['/reports'],
                routerLinkActiveOptions: { exact: false },
            },
            {
                label: 'المخزون',
                icon: 'pi pi-box',
                routerLinkActiveOptions: { exact: false },
                items: [
                    {
                        label: 'المنتجات',
                        routerLink: ['/products'],
                        routerLinkActiveOptions: { exact: false },
                    },
                    {
                        label: 'الفئات',
                        routerLink: ['/categories'],
                        routerLinkActiveOptions: { exact: false },

                    },
                ],

            },
            {
                label: 'المستخدمين',
                icon: 'pi pi-users',
                routerLink: ['/users'],
                routerLinkActiveOptions: { exact: true },
            },
            {
                label: 'الإعدادات',
                icon: 'pi pi-cog',
                routerLink: ['/settings'],
                routerLinkActiveOptions: { exact: true },
            },
        ].filter((i) => !!i);
    }

    getTheCurrentRoute(): void {
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                take(1),
                tap((routerEvent) => {
                    if (routerEvent instanceof NavigationEnd) {
                        this.currentRoute = routerEvent.url.replace('/', '');
                    }
                }),
            )
            .subscribe();
    }

    isRouteActive(routes: string[]): boolean {
        return routes.includes(this.currentRoute);
    }
}
